import React, { useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
} from '@mui/icons-material';
import ReactPlayer from 'react-player';
import { API_URL } from '../../config/api';
import { useAuth } from '../../context/AuthContext';

// Shared full-screen preview for Digital Library images and videos.
// files: the current page's file list (for next/prev navigation)
// index: index of the currently open file within `files`
const LibraryPreviewModal = ({ open, files, index, onClose, onNavigate, onDownload }) => {
  const { tokenManager } = useAuth();
  const file = files && index !== null && index >= 0 ? files[index] : null;
  const viewUrl = file ? `${API_URL}/digital-library/files/${file.fileId}/view?token=${encodeURIComponent(tokenManager.getToken())}` : null;

  const handleKeyDown = useCallback((e) => {
    if (!open) return;
    if (e.key === 'ArrowLeft') onNavigate(index - 1);
    if (e.key === 'ArrowRight') onNavigate(index + 1);
    if (e.key === 'Escape') onClose();
  }, [open, index, onNavigate, onClose]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!file) return null;

  const hasPrev = index > 0;
  const hasNext = files && index < files.length - 1;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { bgcolor: '#111' } }}>
      <DialogTitle sx={{ color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ overflow: 'hidden' }}>
          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>{file.originalName}</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {file.fileSizeFormatted}{file.durationFormatted ? ` • ${file.durationFormatted}` : ''}
          </Typography>
        </Box>
        <Box>
          {file.downloadEnabled && (
            <Tooltip title="Download">
              <IconButton sx={{ color: '#fff' }} onClick={() => onDownload(file)}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton sx={{ color: '#fff' }} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 480, p: 2 }}>
        {hasPrev && (
          <IconButton
            onClick={() => onNavigate(index - 1)}
            sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#fff', bgcolor: 'rgba(0,0,0,0.4)', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' } }}
          >
            <PrevIcon />
          </IconButton>
        )}

        {file.fileType === 'video' ? (
          <Box sx={{ width: '100%', position: 'relative', paddingTop: '56.25%' }}>
            <ReactPlayer
              url={viewUrl}
              width="100%"
              height="100%"
              style={{ position: 'absolute', top: 0, left: 0 }}
              controls
            />
          </Box>
        ) : (
          <Box
            component="img"
            src={viewUrl}
            alt={file.originalName}
            sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
          />
        )}

        {hasNext && (
          <IconButton
            onClick={() => onNavigate(index + 1)}
            sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#fff', bgcolor: 'rgba(0,0,0,0.4)', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' } }}
          >
            <NextIcon />
          </IconButton>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LibraryPreviewModal;
